/* Five-minute reservation display. One deadline per cart, preserved across drawer updates and page reloads. */
class KlCartReservation extends HTMLElement {
  static storageKey='kl-cart-reservation-deadline-v1';
  static deadline=0;
  static clear() {
    KlCartReservation.deadline=0;
    try { localStorage.removeItem(KlCartReservation.storageKey); } catch (_) {}
  }
  connectedCallback() {
    const duration=(Number(this.dataset.duration)||300)*1000;
    let saved=KlCartReservation.deadline;
    try { const stored=Number(localStorage.getItem(KlCartReservation.storageKey)); if(Number.isFinite(stored)&&stored>0)saved=stored; } catch (_) {}
    this.deadline=saved>0?saved:Date.now()+duration;
    KlCartReservation.deadline=this.deadline;
    try { localStorage.setItem(KlCartReservation.storageKey,String(this.deadline)); } catch (_) {}
    this.onStorage=event=>{
      if(event.key!==KlCartReservation.storageKey)return;
      const next=Number(event.newValue);
      this.deadline=Number.isFinite(next)&&next>0?next:Date.now();
      KlCartReservation.deadline=next>0?next:0;
      this.tick();
    };
    window.addEventListener('storage',this.onStorage);
    this.tick();
    this.interval=setInterval(()=>this.tick(),1000);
  }
  disconnectedCallback() {
    clearInterval(this.interval);
    window.removeEventListener('storage',this.onStorage);
  }
  tick() {
    const remaining=Math.max(0,Math.ceil((this.deadline-Date.now())/1000));
    const active=this.querySelector('[data-reservation-active]');
    const expired=this.querySelector('[data-reservation-expired]');
    const clock=this.querySelector('[data-reservation-clock]');
    if(!active||!expired||!clock)return;
    this.hidden=false;
    active.hidden=remaining===0;
    expired.hidden=remaining>0;
    this.classList.toggle('is-expired',remaining===0);
    if(remaining>0){
      const minutes=Math.floor(remaining/60), seconds=remaining%60;
      clock.textContent=String(minutes).padStart(2,'0')+':'+String(seconds).padStart(2,'0');
      clock.setAttribute('aria-label',minutes+' minuten en '+seconds+' seconden resterend');
    }
  }
}
customElements.define('kl-cart-reservation',KlCartReservation);

/* K&L cart: Dawn sections, accessible focus and one cart update at a time. */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onEscape=event=>{if(!event.defaultPrevented && !event.isComposing && event.key==='Escape' && this.classList.contains('active')){event.preventDefault();this.close();}};
    this.addEventListener('click', event => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
      if (event.target.closest('[data-kl-continue]')) this.close();
    });
    this.addEventListener('change', event => {
      if (event.target.matches('[data-kl-insurance]')) this.updateInsurance(event.target);
      if (event.target.matches('[data-kl-accessory]')) this.updateAccessory(event.target);
    });
    this.setHeaderCartIconAccessibility();
  }
  connectedCallback() { document.addEventListener('keydown',this.onEscape); if(this.classList.contains('is-empty'))KlCartReservation.clear(); }
  disconnectedCallback() { document.removeEventListener('keydown',this.onEscape); clearTimeout(this.focusFrame); }
  setHeaderCartIconAccessibility() {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role','button'); link.setAttribute('aria-haspopup','dialog');
    link.addEventListener('click', event => { event.preventDefault(); this.open(link); });
    link.addEventListener('keydown', event => { if (event.code === 'Space') { event.preventDefault(); this.open(link); } });
  }
  focusContents(preferred) {
    clearTimeout(this.focusFrame);
    if (!this.classList.contains('active')) return;
    const dialog=this.querySelector('.drawer__inner');
    const target=preferred ? this.querySelector(preferred) : null;
    if (dialog) trapFocus(dialog,target || dialog);
    this.focusFrame=setTimeout(() => {
      if (!this.classList.contains('active') || this.contains(document.activeElement)) return;
      const current=this.querySelector('.drawer__inner');
      if (current) trapFocus(current,preferred ? this.querySelector(preferred) || current : current);
    },320);
  }
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('animate','active');
    document.body.classList.add('overflow-hidden');
    this.focusContents();
  }
  close() {
    clearTimeout(this.focusFrame);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement && this.activeElement.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    document.body.classList.remove('overflow-hidden');
  }
  setBusy(busy) {
    this.dataset.klBusy = String(busy);
    this.querySelector('.drawer__inner')?.setAttribute('aria-busy',String(busy));
    if (busy) {
      const focused=document.activeElement;
      if(this.classList.contains('active') && this.contains(focused) && focused.matches('.quantity__button,.quantity__input,cart-remove-button button,[data-kl-insurance],[data-kl-accessory],#CartDrawer-Checkout')) this.querySelector('.drawer__close')?.focus({preventScroll:true});
      this.querySelectorAll('.quantity__button,.quantity__input,cart-remove-button button,[data-kl-insurance],[data-kl-accessory],#CartDrawer-Checkout').forEach(control => {
        if (!control.disabled) { control.dataset.klLocked='true'; control.disabled=true; }
      });
    } else {
      this.querySelectorAll('[data-kl-locked]').forEach(control => { control.disabled=false; delete control.dataset.klLocked; });
      if(this.classList.contains('is-empty'))KlCartReservation.clear();
    }
  }
  async updateInsurance(toggle) {
    if (this.dataset.klBusy === 'true') return;
    const desired = toggle.checked, key = toggle.dataset.key || toggle.dataset.variant;
    this.setBusy(true);
    const error = this.querySelector('#KL-InsuranceError'); if (error) error.textContent='';
    try {
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({
        updates: { [key]: desired ? 1 : 0 }, sections: this.getSectionsToRender().map(section=>section.id), sections_url: window.location.pathname
      }) });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(typeof state.description === 'string' ? state.description : 'De keuze kon niet worden bijgewerkt. Probeer het opnieuw.');
      this.renderContents(state,{open:false,focus:'[data-kl-insurance]',preserveView:true});
      publish(PUB_SUB_EVENTS.cartUpdate,{source:'cart-items',cartData:state});
    } catch (error) {
      try {
        const response = await fetch(routes.cart_url + '?sections=cart-drawer,cart-icon-bubble');
        const sections = await response.json();
        this.renderContents({sections},{open:false,focus:'[data-kl-insurance]',preserveView:true});
      } catch (_) { if (toggle.isConnected) toggle.checked=!desired; }
      const message=this.querySelector('#KL-InsuranceError');
      if (message) message.textContent=error.message || 'Bijwerken lukt niet. Controleer je winkelwagen en probeer opnieuw.';
    } finally { this.setBusy(false); }
  }
  async accessoryRequest(url, options) {
    const response=await fetch(url,options);
    const state=await response.json();
    if(!response.ok || state.errors || state.status) throw new Error(typeof state.description==='string' ? state.description : 'Je accessoire kon niet worden bijgewerkt. Probeer het opnieuw.');
    return state;
  }
  async refreshAccessorySections() {
    const sections=await this.accessoryRequest(routes.cart_url+'?sections=cart-drawer,cart-icon-bubble',{cache:'no-store'});
    return {sections};
  }
  async updateAccessory(toggle) {
    if(this.dataset.klBusy==='true'){toggle.checked=toggle.defaultChecked;return;}
    const desired=toggle.checked, variant=Number(toggle.dataset.variant);
    const focus='#'+toggle.id, title=toggle.dataset.title;
    const options={open:false,focus,preserveView:true};
    this.setBusy(true);
    const error=this.querySelector('#KL-AccessoriesError'); if(error)error.textContent='';
    const status=toggle.closest('label')?.querySelector('[data-kl-accessory-status]');
    if(status)status.textContent=desired?'Toevoegen…':'Verwijderen…';
    try {
      // Read current keys first: an existing quantity is never reset to one.
      const current=await this.accessoryRequest(routes.cart_url+'.js',{cache:'no-store'});
      const matches=current.items.filter(item=>Number(item.variant_id)===variant);
      const sections={sections:this.getSectionsToRender().map(section=>section.id),sections_url:window.location.pathname};
      let state;
      if(desired && !matches.length) {
        state=await this.accessoryRequest(routes.cart_add_url,{...fetchConfig(),body:JSON.stringify({...sections,items:[{id:variant,quantity:1}]})});
      } else if(!desired && matches.length) {
        const updates={}; matches.forEach(item=>{updates[item.key]=0;});
        state=await this.accessoryRequest(routes.cart_update_url,{...fetchConfig(),body:JSON.stringify({...sections,updates})});
      } else { state=await this.refreshAccessorySections(); }
      this.renderContents(state,options);
      const live=this.querySelector('#CartDrawer-LiveRegionText');
      if(live){live.removeAttribute('aria-hidden');live.textContent=title+(desired?' toegevoegd aan je winkelwagen.':' verwijderd uit je winkelwagen.');}
      publish(PUB_SUB_EVENTS.cartUpdate,{source:'cart-items',cartData:state,variantId:variant});
    } catch(error) {
      try {this.renderContents(await this.refreshAccessorySections(),options);}
      catch(_) {if(toggle.isConnected){toggle.checked=toggle.defaultChecked;if(status)status.textContent='Probeer opnieuw';}}
      const message=this.querySelector('#KL-AccessoriesError') || this.querySelector('#CartDrawer-CartErrors');
      if(message)message.textContent=error.message || 'Bijwerken lukt niet. Controleer je winkelwagen en probeer opnieuw.';
    } finally {this.setBusy(false);}
  }
  renderContents(state,options={}) {
    const oldInner=this.querySelector('.drawer__inner'), oldItems=this.querySelector('cart-drawer-items');
    const view=options.preserveView ? {inner:oldInner?.scrollTop||0,items:oldItems?.scrollTop||0,extras:this.querySelector('.kl-cart-extras')?.open,anchor:options.focus?this.querySelector(options.focus)?.getBoundingClientRect().top:null} : null;
    const sections = state.sections;
    const next = sections && sections['cart-drawer'] ? new DOMParser().parseFromString(sections['cart-drawer'],'text/html') : null;
    const inner = next?.querySelector('#CartDrawer');
    if (!inner || !sections['cart-icon-bubble']) throw new Error('Je winkelwagen kon niet worden ververst. Probeer opnieuw.');
    const bubble = new DOMParser().parseFromString(sections['cart-icon-bubble'],'text/html').querySelector('.shopify-section');
    if (!bubble) throw new Error('Je winkelwagen kon niet worden ververst. Probeer opnieuw.');
    this.productId=state.id;
    this.querySelector('#CartDrawer').innerHTML=inner.innerHTML;
    this.classList.toggle('is-empty',next.querySelector('cart-drawer').classList.contains('is-empty'));
    if(this.classList.contains('is-empty'))KlCartReservation.clear();
    const icon=document.getElementById('cart-icon-bubble'); if(icon) icon.innerHTML=bubble.innerHTML;
    if(view && this.querySelector('.kl-cart-extras'))this.querySelector('.kl-cart-extras').open=view.extras;
    if (options.open !== false) this.open(); else this.focusContents(options.focus);
    if(view){
      const currentInner=this.querySelector('.drawer__inner'), currentItems=this.querySelector('cart-drawer-items');
      if(currentInner)currentInner.scrollTop=view.inner;if(currentItems)currentItems.scrollTop=view.items;
      const anchor=options.focus?this.querySelector(options.focus):null;
      if(anchor && view.anchor!=null){
        const scroller=currentInner && /auto|scroll/.test(getComputedStyle(currentInner).overflowY) ? currentInner : currentItems;
        if(scroller)scroller.scrollTop+=anchor.getBoundingClientRect().top-view.anchor;
      }
    }
  }
  getSectionInnerHTML(html,selector='.shopify-section') { return new DOMParser().parseFromString(html,'text/html').querySelector(selector).innerHTML; }
  getSectionDOM(html,selector='.shopify-section') { return new DOMParser().parseFromString(html,'text/html').querySelector(selector); }
  getSectionsToRender() { return [{id:'cart-drawer',selector:'#CartDrawer'},{id:'cart-icon-bubble'}]; }
  setActiveElement(element) { this.activeElement=element; }
}
customElements.define('cart-drawer',CartDrawer);
class CartDrawerItems extends CartItems {
  getSectionsToRender() { return [{id:'CartDrawer',section:'cart-drawer',selector:'.drawer__inner'},{id:'cart-icon-bubble',section:'cart-icon-bubble',selector:'.shopify-section'}]; }
}
customElements.define('cart-drawer-items',CartDrawerItems);
